import {
	ActionIcon,
	Button,
	FileInput,
	Flex,
	Group,
	Image,
	Modal,
	SimpleGrid,
	Stack,
	Switch,
	Text,
	TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import {
	IconFileZip,
	IconPlus,
	IconTrash,
	IconUpload,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	createSticker,
	createStickerItemsFromZip,
	deleteStickerItem,
	getListSticker,
	updateSticker,
} from 'src/apis/stickerAPI'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import TableComponent from 'src/components/TableComponent'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { formatDateTime, size } from 'src/variable/constant'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'

function StickerPage() {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [data, setData] = useState<any[]>([])
	const [total, setTotal] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)
	const [createOpen, setCreateOpen] = useState(false)
	const [uploadOpen, setUploadOpen] = useState<{
		open: boolean
		row: any
	}>({
		open: false,
		row: null,
	})
	const [detailOpen, setDetailOpen] = useState<{
		open: boolean
		row: any
	}>({
		open: false,
		row: null,
	})
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})

	const createForm = useForm({
		initialValues: {
			title: '',
		},
		validate: {
			title: (value) =>
				value.trim() ? null : 'Sticker title cannot be empty',
		},
		validateInputOnBlur: true,
	})

	const uploadForm = useForm<{ file: File | null }>({
		initialValues: {
			file: null,
		},
		validate: {
			file: (file) => {
				if (!file) return 'ZIP file is required'
				if (!file.name.toLowerCase().endsWith('.zip')) {
					return 'Only .zip file is accepted'
				}
				return null
			},
		},
		validateInputOnBlur: true,
	})

	const handleGetData = useCallback(async () => {
		let arr: any[] = []
		let count = 0
		setLoading(true)
		try {
			const res = await getListSticker({
				fields: ['$all'],
				page: pagination.pageIndex,
				limit: pagination.pageSize,
			})
			arr = res?.data?.results?.objects?.rows?.map((item, index) => ({
				...item,
				no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
				item_count: item?.sticker_items?.length || 0,
				created_at: dayjs(item?.created_at).format(formatDateTime),
				updated_at: dayjs(item?.updated_at).format(formatDateTime),
			}))
			count = res?.data?.results?.objects?.count || 0
		} catch (error) {
			openError(error)
		} finally {
			setData(arr)
			setTotal(count)
			setLoading(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination)])

	const handleChangePage = useCallback((value: number) => {
		setPagination((pre) => ({
			...pre,
			pageIndex: value,
		}))
	}, [])

	const handleCreateSticker = useCallback(
		async (values: { title: string }) => {
			openLoadingContext()
			try {
				const res = await createSticker({ title: values.title.trim() })
				if (res?.data?.code === 200) {
					createForm.reset()
					setCreateOpen(false)
					if (pagination.pageIndex !== 1) {
						handleChangePage(1)
					} else {
						await handleGetData()
					}
					openModal({
						type: 'success',
						label: 'Sticker has been created successfully',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[handleGetData, pagination.pageIndex],
	)

	const handleUploadZip = useCallback(
		async (values: { file: File | null }) => {
			if (!uploadOpen.row?.id || !values.file) return
			openLoadingContext()
			try {
				const res = await createStickerItemsFromZip(
					uploadOpen.row.id,
					values.file,
				)
				if (res?.data?.code === 200) {
					uploadForm.reset()
					setUploadOpen({ open: false, row: null })
					await handleGetData()
					openModal({
						type: 'success',
						label: 'Sticker items have been created successfully',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[handleGetData, JSON.stringify(uploadOpen.row)],
	)

	const handleToggleStatus = useCallback(
		async (row: any) => {
			const sticker = row.original
			openLoadingContext()
			try {
				const res = await updateSticker(sticker.id, {
					status: !sticker.status,
				})
				if (res?.data?.code === 200) {
					setData((pre) =>
						pre.map((item) =>
							item.id === sticker.id
								? { ...item, status: !sticker.status }
								: item,
						),
					)
					openModal({
						type: 'success',
						label: 'Sticker status updated successfully',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(data)],
	)

	const handleDeleteStickerItem = useCallback(
		(item: any) => {
			if (!detailOpen.row?.id) return
			openModal({
				type: 'confirm',
				label: <div>Do you want to delete this sticker item?</div>,
				handleConfirm: async () => {
					openLoadingContext()
					try {
						const res = await deleteStickerItem(detailOpen.row.id, item.id)
						if (res?.data?.code === 200) {
							const nextItems = (
								detailOpen.row?.sticker_items || []
							).filter((stickerItem) => stickerItem.id !== item.id)
							setDetailOpen((pre) => ({
								...pre,
								row: {
									...pre.row,
									sticker_items: nextItems,
									item_count: nextItems.length,
								},
							}))
							setData((pre) =>
								pre.map((sticker) =>
									sticker.id === detailOpen.row.id
										? {
												...sticker,
												sticker_items: nextItems,
												item_count: nextItems.length,
										  }
										: sticker,
								),
							)
							openModal({
								type: 'success',
								label: 'Sticker item has been deleted successfully',
							})
						}
					} catch (error) {
						openError(error)
					} finally {
						closeLoadingContext()
					}
				},
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(detailOpen.row)],
	)

	const columns = useMemo(
		(): MRT_ColumnDef[] => [
			{
				accessorKey: 'no',
				header: 'No',
				size: size.zero,
			},
			{
				accessorKey: 'title',
				header: 'Title',
				size: size.medium,
			},
			{
				accessorKey: 'item_count',
				header: 'Items',
				size: size.no,
			},
			{
				accessorKey: 'status',
				header: 'Status',
				size: size.no,
				Cell: ({ row }: any) => (
					<Switch
						onLabel="Active"
						offLabel="Paused"
						radius="sm"
						size="xl"
						checked={row.original?.status}
						onChange={() => handleToggleStatus(row)}
						sx={{
							'.mantine-Switch-body': {
								'.mantine-Switch-track': {
									cursor: 'pointer',
									width: 100,
								},
							},
							'.mantine-Switch-trackLabel': {
								fontSize: '0.875rem',
							},
						}}
					/>
				),
			},
			{
				accessorKey: 'created_at',
				header: 'Created at',
				size: size.text,
			},
			{
				accessorKey: 'updated_at',
				header: 'Updated at',
				size: size.text,
			},
		],
		[handleToggleStatus],
	)

	useEffect(() => {
		handleGetData()
	}, [handleGetData])

	return (
		<Stack sx={{ width: '100%' }}>
			<Flex justify="flex-end">
				<CustomButton
					variant="outlined"
					sx={{ height: '36px', whiteSpace: 'nowrap', minWidth: '180px' }}
					onClick={() => setCreateOpen(true)}
				>
					<Group spacing={6}>
						<IconPlus size={18} />
						<span>Create sticker</span>
					</Group>
				</CustomButton>
			</Flex>
			<TableComponent
				columns={columns}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				handleChangePage={handleChangePage}
				handleDetail={(row) =>
					setDetailOpen({ open: true, row: row.original })
				}
				_renderCustomActions={(row) => (
					<CustomTooltip label="Upload ZIP">
						<IconFileZip
							style={{ cursor: 'pointer' }}
							onClick={() => {
								uploadForm.reset()
								setUploadOpen({ open: true, row: row.original })
							}}
						/>
					</CustomTooltip>
				)}
			/>
			<Modal
				opened={createOpen}
				onClose={() => setCreateOpen(false)}
				title="Create sticker"
				centered
			>
				<form onSubmit={createForm.onSubmit(handleCreateSticker)}>
					<Stack>
						<TextInput
							withAsterisk
							label="Title"
							placeholder="Sticker title"
							{...createForm.getInputProps('title')}
						/>
						<Button variant="outline" type="submit">
							Create
						</Button>
					</Stack>
				</form>
			</Modal>
			<Modal
				opened={uploadOpen.open}
				onClose={() => setUploadOpen({ open: false, row: null })}
				title={`Upload ZIP${uploadOpen.row?.title ? ` - ${uploadOpen.row.title}` : ''}`}
				centered
			>
				<form onSubmit={uploadForm.onSubmit(handleUploadZip)}>
					<Stack>
						<FileInput
							withAsterisk
							accept=".zip,application/zip,application/x-zip-compressed"
							label="ZIP file"
							icon={<IconUpload size={18} />}
							{...uploadForm.getInputProps('file')}
						/>
						<Button variant="outline" type="submit">
							Upload
						</Button>
					</Stack>
				</form>
			</Modal>
			<Modal
				opened={detailOpen.open}
				onClose={() => setDetailOpen({ open: false, row: null })}
				title={`Sticker items${
					detailOpen.row?.title ? ` - ${detailOpen.row.title}` : ''
				}`}
				size="lg"
				centered
			>
				{!detailOpen.row?.sticker_items?.length ? (
					<Text c="dimmed">No sticker items</Text>
				) : (
					<SimpleGrid cols={4} spacing="sm">
						{detailOpen.row.sticker_items.map((item) => (
							<Stack key={item.id} spacing={6} align="center">
								<Image
									src={item.url}
									alt={item.title}
									width={84}
									height={84}
									fit="contain"
								/>
								<Text size="xs" lineClamp={1}>
									{item.title}
								</Text>
								<CustomTooltip label="Delete item">
									<ActionIcon
										color="red"
										variant="subtle"
										onClick={() => handleDeleteStickerItem(item)}
									>
										<IconTrash size={18} />
									</ActionIcon>
								</CustomTooltip>
							</Stack>
						))}
					</SimpleGrid>
				)}
			</Modal>
		</Stack>
	)
}

export default React.memo(StickerPage)
